<!--
  StoreOrderNumberFormat.vue

  Card: Order number format: per-store prefix, editable start, suffix
  (AdventHealth) -- https://trello.com/c/38FENeQF

  The "Order Number Format" card on the store-editor Checkout page. Lets a
  distributor control the customer-facing order number for THIS store:

      <PREFIX>-<YEAR>-<NNNNN>[-<SUFFIX>]        e.g.  PA-2026-01037

  Jake's decisions (2026-08-28), verbatim: "1: Per store. 2: leave existing
  orders alone. 3: Keep the year to minimize disruption of existing orders."
  The YEAR segment is retained so the new format stays continuous with the
  existing one; the sequence still resets annually.

  Defaults (spec): the prefix is derived from the first character of the
  first two words of the STORE name (or the first two characters of a single
  word); the starting number is 1000; the suffix is empty.

  DERIVED-vs-CHOSEN, and why null and "" differ here. A NULL prefix means
  "never configured" and the backend derives the default on read; an EMPTY
  STRING means the distributor deliberately turned the prefix off (it is
  optional per the spec). Collapsing the two would make the field impossible
  to clear -- clearing it would silently restore the derived default. The
  same asymmetry is documented on the Store model columns and honoured by
  ``order_number.store_number_config``.

  The derivation below intentionally MIRRORS the backend's
  ``derive_store_prefix``. The backend remains authoritative -- it derives
  the default at allocation time regardless of what this card sends -- so a
  drift here changes only the pre-filled hint a distributor sees, never the
  number an order actually gets.
-->
<template>
  <editor-card
    title="Order Number Format"
    description="Controls the order number shoppers see on this store's confirmations, emails and receipts."
  >
    <div data-testid="ct-order-number-format">
      <v-row>
        <v-col cols="12" sm="3">
          <v-text-field
            label="Prefix (optional)"
            v-model="prefix"
            :rules="[validateAffix]"
            :disabled="campaign.complete"
            data-testid="ct-order-number-prefix"
            hint="Capital letters and numbers only"
            persistent-hint
            @update:modelValue="onFieldChanged"
          />
        </v-col>

        <v-col cols="12" sm="3">
          <v-text-field
            label="Starting number"
            type="number"
            v-model="startNumber"
            :rules="[validateStart]"
            :disabled="campaign.complete"
            data-testid="ct-order-number-start"
            hint="The first order on this store each year"
            persistent-hint
            @update:modelValue="onFieldChanged"
          />
        </v-col>

        <v-col cols="12" sm="3">
          <v-text-field
            label="Suffix (optional)"
            v-model="suffix"
            :rules="[validateAffix]"
            :disabled="campaign.complete"
            data-testid="ct-order-number-suffix"
            hint="Capital letters and numbers only"
            persistent-hint
            @update:modelValue="onFieldChanged"
          />
        </v-col>

        <v-col cols="12" sm="3">
          <div class="order-number-preview-label">Example</div>
          <div
            class="order-number-preview"
            data-testid="ct-order-number-preview"
          >
            {{ preview }}
          </div>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <!--
            Decision 2 made visible. A distributor changing this must not
            believe past orders get restamped -- they keep their numbers and
            the two formats coexist.
          -->
          <div class="order-number-note" data-testid="ct-order-number-note">
            Changing this affects future orders only. Existing orders keep the
            numbers they were given.
          </div>
        </v-col>
      </v-row>
    </div>
  </editor-card>
</template>

<script>
import EditorCard from "./components/EditorCard.vue";

// Mirrors the backend ``order_number.AFFIX_MAX_LENGTH`` / ``_AFFIX_RE``.
const AFFIX_MAX_LENGTH = 12;
const AFFIX_RE = /^[A-Z0-9]+$/;
const DEFAULT_SEQ_START = 1000;
const SEQ_MIN_WIDTH = 5;

/**
 * Derive the default prefix from a store name. Mirrors the backend
 * ``derive_store_prefix``: strip a leading bracketed tag, then take the
 * first character of the first two words, or the first two characters of a
 * single word. Returns "ORD" when the name yields nothing usable.
 */
export function deriveStorePrefix(storeName) {
  const name = String(storeName || "")
    .trim()
    // Strip a single LEADING bracketed tag (e.g. "[TEST] ") so seed markers
    // do not leak into the prefix.
    .replace(/^\s*\[[^\]]*\]\s*/, "")
    .trim();
  if (!name) return "ORD";

  const words = name.split(/[^0-9A-Za-z]+/).filter(Boolean);
  if (!words.length) return "ORD";

  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return words[0].slice(0, 2).toUpperCase();
}

export default {
  name: "StoreOrderNumberFormat",

  components: {
    "editor-card": EditorCard,
  },

  props: {
    campaign: { type: Object, required: true },
    editMode: { type: Boolean, default: false },
  },

  emits: ["campaign-changed"],

  data() {
    return {
      prefix: "",
      suffix: "",
      startNumber: String(DEFAULT_SEQ_START),
    };
  },

  computed: {
    /**
     * Live example of what the next order number will look like. Built from
     * the CURRENT field values so a distributor sees the effect before
     * saving. Uses the current year because the sequence resets annually.
     */
    preview() {
      const year = new Date().getFullYear();
      const start = parseInt(this.startNumber, 10);
      const seq = Number.isFinite(start) && start > 0 ? start : DEFAULT_SEQ_START;
      const padded = String(seq).padStart(SEQ_MIN_WIDTH, "0");

      const parts = [];
      const p = (this.prefix || "").trim();
      if (p) parts.push(p);
      parts.push(`${year}-${padded}`);
      const s = (this.suffix || "").trim();
      if (s) parts.push(s);
      return parts.join("-");
    },
  },

  created() {
    this.loadFromCampaign(this.campaign);
  },

  methods: {
    /**
     * Populate the fields from the store. A NULL prefix means "never
     * configured" -> show the derived default so the distributor sees what
     * they will get. An EMPTY STRING means they turned the prefix off ->
     * leave it empty.
     */
    loadFromCampaign(campaign) {
      const c = campaign || {};
      this.prefix =
        c.order_number_prefix === null || c.order_number_prefix === undefined
          ? deriveStorePrefix(c.name)
          : c.order_number_prefix;
      this.suffix = c.order_number_suffix || "";
      this.startNumber = String(
        c.order_number_start === null || c.order_number_start === undefined
          ? DEFAULT_SEQ_START
          : c.order_number_start
      );
    },

    /**
     * Vuetify rule: true when valid, otherwise the message to display.
     * Both affixes are OPTIONAL, so blank is valid. Trailing/leading
     * whitespace is trimmed rather than rejected (a paste artifact is a
     * typo, not an intent), matching the backend ``normalize_affix``.
     */
    validateAffix(value) {
      const v = String(value == null ? "" : value).trim();
      if (!v) return true;
      if (v.length > AFFIX_MAX_LENGTH) {
        return `Use at most ${AFFIX_MAX_LENGTH} characters`;
      }
      if (!AFFIX_RE.test(v)) {
        return "Use capital letters and numbers only";
      }
      return true;
    },

    /**
     * Vuetify rule for the starting number: a positive whole number.
     */
    validateStart(value) {
      const raw = String(value == null ? "" : value).trim();
      if (!raw) return "Enter a starting number";
      if (!/^\d+$/.test(raw)) return "Enter a whole number";
      if (parseInt(raw, 10) < 1) return "Must be 1 or greater";
      return true;
    },

    onFieldChanged() {
      this.$emit("campaign-changed");
    },

    /**
     * Save-time payload, merged into the campaign by the parent
     * orchestrator. Affixes are uppercased + trimmed so the stored value
     * matches what the backend validator accepts.
     *
     * An empty SUFFIX is sent as null (absent). An empty PREFIX is sent as
     * an empty STRING, because empty means "no prefix" while null would ask
     * the backend to re-derive the default.
     */
    getData() {
      const p = String(this.prefix || "").trim().toUpperCase();
      const s = String(this.suffix || "").trim().toUpperCase();
      const start = parseInt(this.startNumber, 10);

      return {
        order_number_prefix: p,
        order_number_suffix: s || null,
        order_number_start: Number.isFinite(start) ? start : DEFAULT_SEQ_START,
      };
    },
  },
};
</script>

<style scoped>
.order-number-preview-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 4px;
}

.order-number-preview {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.order-number-note {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
}
</style>
